import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RefreshCw, FileSearch, Package, AlertTriangle, Layers, Clock, Trash2, Download, Upload, Globe } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const ROW_TYPE_LABELS = {
  product_first: { label: "Parcel rows", hint: "First row of each parcel", tone: "bg-emerald-100 text-emerald-700" },
  product_continuation: { label: "Continuation rows", hint: "Additional products within the same parcel", tone: "bg-sky-100 text-sky-700" },
  instruction: { label: "Instruction rows", hint: "Packing/shipping notes", tone: "bg-amber-100 text-amber-700" },
  address: { label: "Address rows", hint: "Address continuation lines", tone: "bg-slate-100 text-slate-600" },
  cancelled: { label: "Cancelled rows", hint: "Explicitly marked cancelled", tone: "bg-rose-100 text-rose-700" },
};

const UploadAuditPanel = () => {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Delete / cascade state
  const [deleteTarget, setDeleteTarget] = useState(null); // the audit row being deleted
  const [deleteImpact, setDeleteImpact] = useState(null); // { rows_affected, trackings_preview, ... }
  const [impactLoading, setImpactLoading] = useState(false);
  const [cascadeOrders, setCascadeOrders] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const loadAudits = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BACKEND_URL}/api/shipment-costing/upload-audits?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load audits");
      const data = await res.json();
      setAudits(data.audits || []);
    } catch (e) {
      toast.error("Could not load upload audits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAudits();
  }, []);

  const openDetail = async (batchId) => {
    setSelected(batchId);
    setDetail(null);
    setDetailLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BACKEND_URL}/api/shipment-costing/upload-audits/${batchId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load audit detail");
      const data = await res.json();
      setDetail(data);
    } catch (e) {
      toast.error("Could not load audit details");
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const openDelete = async (audit) => {
    setDeleteTarget(audit);
    setDeleteImpact(null);
    setCascadeOrders(true);
    setImpactLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BACKEND_URL}/api/shipment-costing/upload-audits/${audit.batch_id}/impact`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Failed to load impact");
      const data = await res.json();
      setDeleteImpact(data);
    } catch (e) {
      toast.error("Could not load delete impact");
      setDeleteTarget(null);
    } finally {
      setImpactLoading(false);
    }
  };

  const downloadUnparsedDatesCsv = async (batchId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BACKEND_URL}/api/shipment-costing/upload-audits/${batchId}/unparsed-dates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Download failed");
      }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const nameMatch = disposition.match(/filename="([^"]+)"/);
      const filename = nameMatch ? nameMatch[1] : `unparsed-dates_${batchId.slice(0, 8)}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Unparsed-rows CSV downloaded");
    } catch (e) {
      toast.error(e.message || "Could not download CSV");
    }
  };

  const uploadPatchedDatesCsv = async (batchId, fileObj) => {
    if (!fileObj) return;
    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", fileObj);
      const res = await fetch(
        `${BACKEND_URL}/api/shipment-costing/upload-audits/${batchId}/patch-dates`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Patch failed");
      const bits = [];
      if (data.patched_rows) bits.push(`patched ${data.patched_rows.toLocaleString()}`);
      if (data.skipped_invalid_date) bits.push(`${data.skipped_invalid_date} invalid`);
      if (data.skipped_no_tracking_match) bits.push(`${data.skipped_no_tracking_match} not matched`);
      if (data.skipped_empty) bits.push(`${data.skipped_empty} empty`);
      toast.success(bits.length ? `Dates ${bits.join(", ")}` : "No rows patched");
      // Refresh the detail view so coverage updates immediately
      await openDetail(batchId);
    } catch (e) {
      toast.error(e.message || "Could not patch dates");
    }
  };

  const recostPatchedRows = async (batchId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BACKEND_URL}/api/shipment-costing/upload-audits/${batchId}/recost`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Re-cost failed");
      const bits = [];
      if (data.recosted) bits.push(`re-costed ${data.recosted.toLocaleString()}`);
      if (data.no_rate_found) bits.push(`${data.no_rate_found} no matching rate`);
      if (data.skipped_missing_data) bits.push(`${data.skipped_missing_data} missing data`);
      if (data.sales_updated) bits.push(`${data.sales_updated.toLocaleString()} sales synced`);
      if (data.recosted === 0 && data.eligible_rows === 0) {
        toast.info("No patched rows to re-cost in this batch");
      } else {
        toast.success(bits.length ? bits.join(" • ") : "Nothing to re-cost");
      }
      await openDetail(batchId);
    } catch (e) {
      toast.error(e.message || "Could not re-cost rows");
    }
  };


  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem("token");
      const url = `${BACKEND_URL}/api/shipment-costing/upload-audits/${deleteTarget.batch_id}?cascade=${cascadeOrders}`;
      const res = await fetch(url, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "Delete failed");
      }
      toast.success(
        cascadeOrders
          ? `Deleted audit + ${data.deleted_rows?.toLocaleString?.() ?? 0} order rows`
          : "Deleted audit (order rows kept)"
      );
      setDeleteTarget(null);
      setDeleteImpact(null);
      await loadAudits();
    } catch (e) {
      toast.error(e.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card data-testid="upload-audit-panel" className="border-slate-200">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileSearch className="w-5 h-5 text-indigo-600" />
            Upload Audit
          </CardTitle>
          <CardDescription>
            Per-upload breakdown of rows parsed, parcels detected, row categories, and duplicates skipped.
          </CardDescription>
        </div>
        <Button
          data-testid="refresh-audits-btn"
          variant="outline"
          size="sm"
          onClick={loadAudits}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading audits...
          </div>
        ) : audits.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            No upload audits yet. Run an order-sheet upload to generate one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="audits-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead className="text-right">Rows</TableHead>
                  <TableHead className="text-right">Parcels</TableHead>
                  <TableHead className="text-right">Saved</TableHead>
                  <TableHead className="text-right">Skipped</TableHead>
                  <TableHead className="text-right">Date cov.</TableHead>
                  <TableHead className="text-right">Time</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audits.map((a) => {
                  const cov = a.date_coverage;
                  const covPct = cov?.date_coverage_pct;
                  const covMissing = cov?.rows_without_date || 0;
                  const covTone =
                    covPct == null
                      ? "text-slate-400"
                      : covPct >= 100
                      ? "text-emerald-700"
                      : covPct >= 95
                      ? "text-amber-600"
                      : "text-rose-700 font-semibold";
                  return (
                  <TableRow key={a.batch_id} data-testid={`audit-row-${a.batch_id}`}>
                    <TableCell className="text-sm">{formatTime(a.uploaded_at)}</TableCell>
                    <TableCell className="font-medium text-slate-800 max-w-[280px] truncate" title={a.filename}>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="truncate">{a.filename || "(unknown)"}</span>
                        {a.stitch_stats?.rows_stitched > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 text-[10px] font-semibold border border-indigo-200 shrink-0"
                            title={`${a.stitch_stats.rows_stitched} continuation rows stitched across ${a.stitch_stats.parcels_with_continuation} multi-product parcels`}
                            data-testid={`stitch-badge-${a.batch_id}`}
                          >
                            <Layers className="w-2.5 h-2.5" />
                            +{a.stitch_stats.rows_stitched.toLocaleString()}
                          </span>
                        )}
                        {a.stitch_stats?.locale_correction?.applied && (
                          <span
                            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold border shrink-0 ${
                              a.stitch_stats.locale_correction.reverted
                                ? "bg-slate-100 text-slate-600 border-slate-200 line-through"
                                : "bg-amber-100 text-amber-800 border-amber-200"
                            }`}
                            title={
                              a.stitch_stats.locale_correction.reverted
                                ? `Locale correction REVERTED by ${a.stitch_stats.locale_correction.reverted_by || "user"}`
                                : `Date locale auto-corrected (${a.stitch_stats.locale_correction.rows_swapped} rows). Reason: ${a.stitch_stats.locale_correction.reason}`
                            }
                            data-testid={`locale-badge-${a.batch_id}`}
                          >
                            <Globe className="w-2.5 h-2.5" />
                            DMY
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{a.uploaded_by || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{a.total_rows?.toLocaleString?.() ?? 0}</TableCell>
                    <TableCell className="text-right tabular-nums">{a.total_parcels?.toLocaleString?.() ?? 0}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-700">
                      {a.rows_saved?.toLocaleString?.() ?? 0}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-amber-700">
                      {a.duplicates_skipped?.toLocaleString?.() ?? 0}
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums text-xs ${covTone}`}
                      data-testid={`date-coverage-${a.batch_id}`}
                      title={
                        covPct == null
                          ? "Date coverage not recorded for this upload"
                          : `${covMissing.toLocaleString()} row(s) had a date value in the sheet but the parser could not understand it.`
                      }
                    >
                      {covPct == null ? "—" : `${covPct.toFixed(1)}%`}
                      {covMissing > 0 && (
                        <span className="ml-1 text-[10px] text-rose-500">
                          (-{covMissing.toLocaleString()})
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-xs text-slate-500">
                      {a.processing_time_seconds ? `${a.processing_time_seconds.toFixed(1)}s` : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetail(a.batch_id)}
                          data-testid={`view-audit-${a.batch_id}`}
                        >
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          onClick={() => openDelete(a)}
                          data-testid={`delete-audit-${a.batch_id}`}
                          title="Delete audit and its order rows"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-3xl" data-testid="audit-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="w-5 h-5 text-indigo-600" />
              Audit detail
            </DialogTitle>
            <DialogDescription>
              Full breakdown of a single upload batch.
            </DialogDescription>
          </DialogHeader>
          {detailLoading || !detail ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading...
            </div>
          ) : (
            <ScrollArea className="max-h-[65vh]">
              <div className="space-y-5 pr-2">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat icon={Package} label="Rows parsed" value={detail.total_rows} tone="text-slate-800" />
                  <Stat icon={Layers} label="Parcels" value={detail.total_parcels} tone="text-indigo-700" />
                  <Stat icon={Package} label="Saved" value={detail.rows_saved} tone="text-emerald-700" />
                  <Stat icon={AlertTriangle} label="Skipped dups" value={detail.duplicates_skipped} tone="text-amber-700" />
                </div>

                <div className="text-sm text-slate-600 flex flex-wrap gap-x-6 gap-y-1">
                  <span><span className="font-medium text-slate-500">File:</span> {detail.filename || "—"}</span>
                  <span><span className="font-medium text-slate-500">By:</span> {detail.uploaded_by || "—"}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {detail.processing_time_seconds ? `${detail.processing_time_seconds.toFixed(1)}s` : "—"}
                  </span>
                  <span><span className="font-medium text-slate-500">Uploaded:</span> {formatTime(detail.uploaded_at)}</span>
                  {detail.save_mode && (
                    <span><span className="font-medium text-slate-500">Mode:</span> {detail.save_mode}</span>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Row types</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(detail.row_types || {}).map(([k, v]) => {
                      const meta = ROW_TYPE_LABELS[k] || { label: k, hint: "", tone: "bg-slate-100 text-slate-700" };
                      return (
                        <div
                          key={k}
                          className={`px-3 py-1.5 rounded-md text-sm ${meta.tone}`}
                          title={meta.hint}
                          data-testid={`row-type-${k}`}
                        >
                          <span className="font-semibold">{v.toLocaleString()}</span> {meta.label}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {detail.stitch_stats && (detail.stitch_stats.rows_stitched > 0 || detail.stitch_stats.rows_dropped_no_carry > 0) && (
                  <div
                    className="border border-indigo-200 bg-indigo-50 rounded-md p-3"
                    data-testid="stitch-stats-panel"
                  >
                    <div className="flex items-start gap-2">
                      <Layers className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-indigo-800">
                          Continuation rows stitched
                        </h4>
                        <p className="text-xs text-indigo-700 mt-0.5">
                          <span className="font-semibold">
                            {(detail.stitch_stats.rows_stitched || 0).toLocaleString()}
                          </span>{" "}
                          product row{detail.stitch_stats.rows_stitched === 1 ? "" : "s"} were
                          reunited with their parent parcel across{" "}
                          <span className="font-semibold">
                            {(detail.stitch_stats.parcels_with_continuation || 0).toLocaleString()}
                          </span>{" "}
                          multi-product tracking
                          {detail.stitch_stats.parcels_with_continuation === 1 ? "" : "s"}.{" "}
                          {detail.stitch_stats.rows_dropped_no_carry > 0 ? (
                            <span className="text-rose-700">
                              {detail.stitch_stats.rows_dropped_no_carry.toLocaleString()} orphan row
                              {detail.stitch_stats.rows_dropped_no_carry === 1 ? "" : "s"} had no parent parcel to attach to.
                            </span>
                          ) : (
                            <span className="text-emerald-700">No orphan rows.</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {detail.stitch_stats?.locale_correction?.applied && (
                  <div
                    className="border border-amber-200 bg-amber-50 rounded-md p-3"
                    data-testid="locale-correction-panel"
                  >
                    <div className="flex items-start gap-2">
                      <Globe className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-amber-900">
                          Date locale auto-corrected (US-Excel mis-read rescued)
                        </h4>
                        <p className="text-xs text-amber-800 mt-0.5">
                          Excel saved this workbook with the date column in <strong>MM/DD</strong>{" "}
                          format, even though the user typed dates in <strong>DD/MM</strong>.
                          EximFlow detected this and swapped{" "}
                          <span className="font-semibold">
                            {(detail.stitch_stats.locale_correction.rows_swapped || 0).toLocaleString()}
                          </span>{" "}
                          row{detail.stitch_stats.locale_correction.rows_swapped === 1 ? "" : "s"}{" "}
                          on column{" "}
                          <code className="bg-white border border-amber-200 px-1 py-0.5 rounded text-[10px]">
                            {detail.stitch_stats.locale_correction.column || "OK PARCEL DT"}
                          </code>
                          {" "}so March parcels stayed in March.
                        </p>
                        <p className="text-[10px] text-amber-700 mt-1 italic">
                          Detection signal: {detail.stitch_stats.locale_correction.reason || "n/a"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {detail.date_coverage && (detail.date_coverage.rows_without_date > 0 || detail.date_coverage.date_coverage_pct < 100) && (
                  <div
                    className="border border-amber-300 bg-amber-50 rounded-md p-3"
                    data-testid="date-coverage-warning"
                  >
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-amber-800">
                          Date coverage: {detail.date_coverage.date_coverage_pct?.toFixed(1)}%
                        </h4>
                        <p className="text-xs text-amber-700 mt-0.5">
                          {detail.date_coverage.rows_without_date?.toLocaleString()} of{" "}
                          {(
                            (detail.date_coverage.rows_with_date || 0) +
                            (detail.date_coverage.rows_without_date || 0)
                          ).toLocaleString()}{" "}
                          row(s) had a date value in the sheet but the parser couldn't
                          read it. Those rows won't show up in date-filtered reports.
                        </p>
                        {detail.date_coverage.unparsed_samples?.length > 0 && (
                          <div className="mt-2">
                            <div className="text-[11px] font-medium text-amber-700 mb-1">
                              Sample unparsed rows ({Math.min(detail.date_coverage.unparsed_samples.length, 10)} shown):
                            </div>
                            <div className="grid gap-1 max-h-44 overflow-y-auto">
                              {detail.date_coverage.unparsed_samples.slice(0, 10).map((u, i) => (
                                <div key={i} className="text-[11px] text-amber-900 bg-white/70 rounded px-2 py-1 flex items-center gap-2 font-mono">
                                  {u.sheet && <span className="text-amber-600">{u.sheet}</span>}
                                  {u.tracking && <span className="text-slate-700">{u.tracking}</span>}
                                  <span className="text-rose-700">raw: "{u.raw_parcel_dt}"</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-400 text-amber-800 hover:bg-amber-100 h-7"
                            onClick={() => downloadUnparsedDatesCsv(detail.batch_id)}
                            data-testid="download-unparsed-dates-csv"
                          >
                            <Download className="w-3.5 h-3.5 mr-1.5" />
                            Download unparsed rows CSV
                          </Button>
                          <label
                            className="inline-flex items-center h-7 px-3 rounded-md border border-emerald-400 text-emerald-800 bg-white hover:bg-emerald-50 text-sm font-medium cursor-pointer"
                            data-testid="patch-dates-upload-label"
                            title="Upload the CSV after filling in the Suggested Fix column"
                          >
                            <Upload className="w-3.5 h-3.5 mr-1.5" />
                            Upload fixed CSV
                            <input
                              type="file"
                              accept=".csv,text/csv"
                              className="hidden"
                              data-testid="patch-dates-file-input"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) uploadPatchedDatesCsv(detail.batch_id, f);
                                e.target.value = "";
                              }}
                            />
                          </label>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-indigo-400 text-indigo-800 hover:bg-indigo-50 h-7"
                            onClick={() => recostPatchedRows(detail.batch_id)}
                            data-testid="recost-patched-rows"
                            title="Re-run shipping & total cost for every tracking whose date was patched"
                          >
                            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                            Re-cost patched rows
                          </Button>
                          <span className="text-[11px] text-amber-700">
                            Fix dates in the CSV → upload → re-cost to refresh shipping prices.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {detail.errors_breakdown?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Errors encountered</h4>
                    <div className="flex flex-wrap gap-2">
                      {detail.errors_breakdown.map((e) => (
                        <Badge key={e.type} variant="outline" className="border-rose-200 text-rose-700 bg-rose-50">
                          {e.type}: <span className="font-semibold ml-1">{e.count}</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {detail.sheets?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Per-sheet breakdown</h4>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Sheet</TableHead>
                            <TableHead className="text-right">Rows</TableHead>
                            <TableHead className="text-right">Parcels</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {detail.sheets.map((s) => (
                            <TableRow key={s.name}>
                              <TableCell className="font-medium">{s.name || "(default)"}</TableCell>
                              <TableCell className="text-right tabular-nums">{s.rows?.toLocaleString?.()}</TableCell>
                              <TableCell className="text-right tabular-nums">{s.parcels?.toLocaleString?.()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {detail.new_ledgers_created?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">
                      New ledgers auto-created ({detail.new_ledgers_created.length})
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {detail.new_ledgers_created.map((code) => (
                        <Badge key={code} variant="secondary">{code}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deleting) {
            setDeleteTarget(null);
            setDeleteImpact(null);
          }
        }}
      >
        <DialogContent className="max-w-xl" data-testid="delete-audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="w-5 h-5" />
              Delete upload audit
            </DialogTitle>
            <DialogDescription>
              This removes the audit record. Optionally also removes every order
              row in Shipment Costing that was created by this upload batch.
            </DialogDescription>
          </DialogHeader>

          {impactLoading || !deleteImpact ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Checking impact...
            </div>
          ) : (
            <div className="space-y-4">
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 text-sm space-y-1">
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">File</span>
                  <span className="font-medium text-slate-800 truncate" title={deleteImpact.filename}>
                    {deleteImpact.filename || "—"}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">Uploaded</span>
                  <span className="text-slate-800">{formatTime(deleteImpact.uploaded_at)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">By</span>
                  <span className="text-slate-800">{deleteImpact.uploaded_by || "—"}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Stat icon={Package} label="Order rows affected" value={deleteImpact.rows_affected} tone="text-rose-700" />
                <Stat icon={Layers} label="Distinct trackings" value={deleteImpact.distinct_trackings} tone="text-slate-800" />
              </div>

              {deleteImpact.trackings_preview?.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-1.5">
                    Sample trackings that will be removed
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                    {deleteImpact.trackings_preview.map((t) => (
                      <span
                        key={t}
                        className="inline-block bg-rose-50 border border-rose-100 text-rose-700 px-1.5 py-0.5 rounded text-xs font-mono"
                      >
                        {t}
                      </span>
                    ))}
                    {deleteImpact.distinct_trackings > deleteImpact.trackings_preview.length && (
                      <span className="text-slate-500 text-xs self-center">
                        +{(deleteImpact.distinct_trackings - deleteImpact.trackings_preview.length).toLocaleString()} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              <label
                className="flex items-start gap-2 text-sm border border-amber-200 bg-amber-50 rounded-md p-3 cursor-pointer"
                data-testid="cascade-toggle"
              >
                <input
                  type="checkbox"
                  checked={cascadeOrders}
                  onChange={(e) => setCascadeOrders(e.target.checked)}
                  className="mt-0.5 accent-rose-600"
                />
                <span className="text-slate-700">
                  Also delete <span className="font-semibold text-rose-700">{(deleteImpact.rows_affected || 0).toLocaleString()}</span> order rows from Shipment Costing.
                  <br />
                  <span className="text-xs text-slate-500">
                    Uncheck to keep the rows but remove the audit history entry only.
                  </span>
                </span>
              </label>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteTarget(null);
                setDeleteImpact(null);
              }}
              disabled={deleting}
              data-testid="cancel-delete-audit"
            >
              Cancel
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={confirmDelete}
              disabled={deleting || impactLoading || !deleteImpact}
              data-testid="confirm-delete-audit"
            >
              {deleting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-1.5" />
                  {cascadeOrders
                    ? `Delete audit + ${(deleteImpact?.rows_affected || 0).toLocaleString()} rows`
                    : "Delete audit only"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="border border-slate-200 rounded-lg p-3">
    <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
    <div className={`text-xl font-semibold tabular-nums ${tone || "text-slate-800"}`}>
      {(value ?? 0).toLocaleString?.() ?? value}
    </div>
  </div>
);

export default UploadAuditPanel;
